import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, X, Save, KeyRound, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const TOGGLE_LABELS = {
  push_notifications: "Push Notifications",
  well_sync: "The Well Sync",
  sso_login: "Precipice ID SSO Login",
  audit_logging: "Audit Logging",
  blob_encryption: "Blob Encryption at Rest",
  maintenance_mode: "Maintenance Mode",
};

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

const inputCls = "bg-background border-border rounded-sm font-mono text-sm focus-visible:ring-1 focus-visible:ring-primary";

export default function Configuration() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["config"],
    queryFn: async () => (await api.get("/config")).data,
  });

  const [form, setForm] = useState(null);
  const [newOrigin, setNewOrigin] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data && !form) setForm(structuredClone(data));
  }, [data]); // eslint-disable-line

  if (isLoading || !form)
    return <div className="font-mono text-xs text-muted-foreground animate-pulse">Loading configuration…</div>;

  const save = async (payload, msg) => {
    setSaving(true);
    try {
      const { data: updated } = await api.put("/config", payload);
      setForm((f) => ({ ...f, ...updated }));
      qc.invalidateQueries({ queryKey: ["config"] });
      qc.invalidateQueries({ queryKey: ["services"] });
      toast.success(msg || "Configuration saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addOrigin = () => {
    const v = newOrigin.trim();
    if (!v) return;
    if (form.cors_origins.includes(v)) return toast.error("Origin already exists");
    setForm((f) => ({ ...f, cors_origins: [...f.cors_origins, v] }));
    setNewOrigin("");
  };
  const removeOrigin = (o) =>
    setForm((f) => ({ ...f, cors_origins: f.cors_origins.filter((x) => x !== o) }));

  const toggle = (key) => {
    const next = { ...form.feature_toggles, [key]: !form.feature_toggles[key] };
    setForm((f) => ({ ...f, feature_toggles: next }));
    save({ feature_toggles: next }, `${TOGGLE_LABELS[key]} ${next[key] ? "enabled" : "disabled"}`);
  };

  return (
    <Tabs defaultValue="general" className="max-w-4xl">
      <TabsList className="bg-card border border-border rounded-sm p-1 mb-6">
        {["general", "cors", "features", "push", "storage"].map((t) => (
          <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}
            className="rounded-sm data-[state=active]:bg-primary/15 data-[state=active]:text-white font-mono text-xs uppercase tracking-[0.08em]">
            {t}
          </TabsTrigger>
        ))}
      </TabsList>

      {/* General */}
      <TabsContent value="general" className="space-y-6">
        <div className="border border-border rounded-sm bg-card p-6 space-y-5">
          <Field label="The Well URL">
            <Input data-testid="cfg-well-url" className={inputCls} value={form.well_url || ""}
              onChange={(e) => setForm((f) => ({ ...f, well_url: e.target.value }))} />
          </Field>
          <Field label="Precipice ID SSO URL">
            <Input data-testid="cfg-sso-url" className={inputCls} value={form.sso_url || ""}
              onChange={(e) => setForm((f) => ({ ...f, sso_url: e.target.value }))} />
          </Field>
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="border border-border rounded-sm p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Deploy Host</div>
              <div className="font-mono text-sm mt-1">{form.readonly?.deploy_host}</div>
            </div>
            <div className="border border-border rounded-sm p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Mongo DB</div>
              <div className="font-mono text-sm mt-1">{form.readonly?.mongo_db}</div>
            </div>
          </div>
          <Button data-testid="save-general" disabled={saving} onClick={() => save({ well_url: form.well_url, sso_url: form.sso_url })}
            className="rounded-sm bg-primary hover:bg-primary/90 transition-colors duration-200">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save
          </Button>
        </div>
      </TabsContent>

      {/* CORS */}
      <TabsContent value="cors" className="space-y-6">
        <div className="border border-border rounded-sm bg-card p-6">
          <h3 className="text-sm font-medium mb-1">Allowed CORS Origins</h3>
          <p className="text-xs text-muted-foreground mb-5">Domains permitted to call the Forge API with credentials.</p>
          <div className="space-y-2 mb-4" data-testid="cors-list">
            {form.cors_origins.map((o) => (
              <div key={o} className="flex items-center justify-between border border-border rounded-sm px-3 py-2 bg-background">
                <span className="font-mono text-sm">{o}</span>
                <button data-testid={`remove-origin`} onClick={() => removeOrigin(o)}
                  className="text-muted-foreground hover:text-destructive transition-colors duration-200">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mb-5">
            <Input data-testid="cors-input" className={inputCls} placeholder="https://example.com" value={newOrigin}
              onChange={(e) => setNewOrigin(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addOrigin())} />
            <Button data-testid="add-origin" onClick={addOrigin} variant="secondary" className="rounded-sm shrink-0">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <Button data-testid="save-cors" disabled={saving} onClick={() => save({ cors_origins: form.cors_origins }, "CORS origins saved")}
            className="rounded-sm bg-primary hover:bg-primary/90 transition-colors duration-200">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save Origins
          </Button>
        </div>
      </TabsContent>

      {/* Features */}
      <TabsContent value="features">
        <div className="border border-border rounded-sm bg-card divide-y divide-border" data-testid="feature-toggles">
          {Object.entries(form.feature_toggles).map(([key, val]) => (
            <div key={key} className="flex items-center justify-between px-6 py-4">
              <div>
                <div className="text-sm">{TOGGLE_LABELS[key] || key}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground mt-0.5">{key}</div>
              </div>
              <Switch checked={val} onCheckedChange={() => toggle(key)} data-testid={`toggle-${key}`} />
            </div>
          ))}
        </div>
      </TabsContent>

      {/* Push / VAPID */}
      <TabsContent value="push" className="space-y-6">
        <div className="border border-border rounded-sm bg-card p-6 space-y-5">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-medium">VAPID / Web Push</h3>
          </div>
          <Field label="VAPID Public Key (read-only)">
            <Input readOnly data-testid="vapid-public" className={`${inputCls} opacity-70`} value={form.readonly?.vapid_public_key || ""} />
          </Field>
          <div className="flex items-center gap-2 text-xs">
            <span className={`w-2 h-2 rounded-full ${form.readonly?.vapid_private_key_set ? "bg-emerald-500" : "bg-destructive"}`} />
            <span className="text-muted-foreground font-mono">
              Private key {form.readonly?.vapid_private_key_set ? "configured" : "missing"}
            </span>
          </div>
          <Field label="VAPID Subject">
            <Input data-testid="cfg-vapid-subject" className={inputCls} value={form.vapid_subject || ""}
              onChange={(e) => setForm((f) => ({ ...f, vapid_subject: e.target.value }))} />
          </Field>
          <Button data-testid="save-push" disabled={saving} onClick={() => save({ vapid_subject: form.vapid_subject }, "Push settings saved")}
            className="rounded-sm bg-primary hover:bg-primary/90 transition-colors duration-200">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save
          </Button>
        </div>
      </TabsContent>

      {/* Storage */}
      <TabsContent value="storage" className="space-y-6">
        <div className="border border-border rounded-sm bg-card p-6 space-y-5">
          <h3 className="text-sm font-medium">Blob Storage</h3>
          <Field label="Storage Directory">
            <Input data-testid="cfg-blob-dir" className={inputCls} value={form.blob_storage_dir || ""}
              onChange={(e) => setForm((f) => ({ ...f, blob_storage_dir: e.target.value }))} />
          </Field>
          <div className="flex items-center gap-2 text-xs">
            <span className={`w-2 h-2 rounded-full ${form.readonly?.shared_secret_set ? "bg-emerald-500" : "bg-destructive"}`} />
            <span className="text-muted-foreground font-mono">
              Forge↔Well shared secret {form.readonly?.shared_secret_set ? "configured" : "missing"}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs" data-testid="blob-encryption-status">
            <span className={`w-2 h-2 rounded-full ${form.readonly?.blob_encryption_key_set ? "bg-emerald-500" : "bg-destructive"}`} />
            <span className="text-muted-foreground font-mono">
              At-rest encryption key {form.readonly?.blob_encryption_key_set ? "active (AES/Fernet)" : "missing — files stored in plaintext"}
            </span>
          </div>
          <Button data-testid="save-storage" disabled={saving} onClick={() => save({ blob_storage_dir: form.blob_storage_dir }, "Storage settings saved")}
            className="rounded-sm bg-primary hover:bg-primary/90 transition-colors duration-200">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save
          </Button>
        </div>
      </TabsContent>
    </Tabs>
  );
}
